import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  cleaningEffectivness,
  cleaningEffectivnessStatus,
} from "../../network/cipAPI";
import Charts from "../../FileUpload/chart";
import {
  pressureFormatter,
  dateFormatter,
  typeFormatter,
  getSortedData,
} from "../../utils/tableDataFormatter";
import PageLoading from "../../components/PageLoading";
import { useDispatch, useSelector } from "react-redux";
import {
  storeClenaingEffectiness,
  storeClenaingEffectinessThreshold,
} from "../../Store/TemplateStore/template.action";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import { XlsNormalizefileKey } from "../../utils/constantsString";
import { STATUS_API_ERROR_KEYS } from "../../constants/global.constants";
import { useTranslation } from "react-i18next";
import { extractPlantId, getIndex } from "../../utils/Utils";
import AdvanceSettings from "../analyseData/advancedSettings";
import DupontButton from "../../components/DupontButton";
import WPAgGrid from "../../packages/WPAgGrid/WPAgGrid";
import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";
import IconButton from "../../packages/IconButton";
import { QuestionIcon } from "../../assets/icons";
import CustomModal from "../../packages/CustomModal";
import NoStablePointsDialog from "../../components/NoStablePointsDialog";

import "./index.scss";

const HistoricalCIPEffectivness = ({
  activeStep,
  setSettingsButton,
  setDisableNextButton,
}) => {
  const [rowData, setRowData] = useState(
    useSelector((state) => state.cleaningEffectiness),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const [processedPercentage, setprocessedPercentage] = useState(false);
  const [threshold, setThreshold] = useState(null);
  const [isNoStablePointsDialogOpen, setIsNoStablePointsDialogOpen] =
    useState(false);

  const {
    cleanData: cleaningResponse,
    selTrain: selectedTrain,
    cleanDate: date,
    cips,
    foulingType,
  } = useSelector((state) => state);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const intervalIdRef = useRef(null);
  const plantId = extractPlantId();

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const handleSettingsDialog = (open) => {
    setIsSettingsDialogOpen(open);
  };

  useEffect(() => {
    setSettingsButton?.(
      <DupontButton
        onClick={() => handleSettingsDialog(true)}
        disabled={isLoading}
      >
        {t("analyseData.settings")}
      </DupontButton>,
    );
    return () => setSettingsButton?.(null);
  }, [isLoading]);

  const reasonFormatter = (params) => {
    const { data } = params;
    const reason = data?.reason;
    if (
      !reason ||
      (Array.isArray(reason) && reason.length === 0) ||
      (typeof reason === "string" && !reason.trim())
    ) {
      return t("foulingStrings.noReason");
    }

    const typeArr = Array.isArray(reason)
      ? reason
          .filter(Boolean)
          .map((item) => String(item).trim())
          .filter(Boolean)
      : reason
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);

    if (!typeArr.length) {
      return t("foulingStrings.noReason");
    }

    return typeArr.map((item) => foulingType?.[item] || item).join(", ");
  };

  const colDefs = [
    {
      headerName: "",
      children: [
        {
          headerName: t("HistoricalCIPEffectivnessTableStrings.index"),
          valueGetter: (params) => getIndex(params, params.data.start_date),
          flex: 0.5,
        },
        {
          headerName: t("HistoricalCIPEffectivnessTableStrings.type"),
          field: "type",
          sortable: true,
          flex: 0.5,
          valueFormatter: (params) => typeFormatter(params, t),
        },
        {
          headerName: t("HistoricalCIPEffectivnessTableStrings.start_date"),
          field: "start_date",
          sortable: true,
          flex: 0.75,
          valueFormatter: dateFormatter,
        },
        {
          headerName: t("HistoricalCIPEffectivnessTableStrings.reason"),
          field: "reason",
          sortable: true,
          flex: 1.8,
          valueFormatter: reasonFormatter,
          valueGetter: reasonFormatter,
        },
      ],
    },
    {
      headerName: t("cleaningEffectivnessStrings.cleaningEffectiveness"),
      children: [
        {
          headerName: t("HistoricalCIPEffectivnessTableStrings.pressure_drop"),
          field: "pressure_drop",
          sortable: true,
          flex: 1,
          valueFormatter: pressureFormatter,
          useValueFormatterForExport: true,
          headerClass: "left-divider",
          cellClass: "left-divider",
        },
        {
          headerName: t("HistoricalCIPEffectivnessTableStrings.flow"),
          field: "flow",
          sortable: true,
          flex: 1,
          valueFormatter: pressureFormatter,
          useValueFormatterForExport: true,
        },
        {
          headerName: t("HistoricalCIPEffectivnessTableStrings.salt_passage"),
          field: "salt_passage",
          sortable: true,
          flex: 1,
          valueFormatter: pressureFormatter,
        },
      ],
    },
  ];

  const completed_state = (responseData) => {
    const data = JSON.parse(responseData.result);
    const result = Object.keys(data?.cip).map((key, index) => ({
      start_date: data.cip[index]["sd"],
      type: data.cip[index]["type"],
      pressure_drop:
        data.norm_pressure_drop_flag[index] === 1
          ? `${data[XlsNormalizefileKey.normalizeDPRack2][index]}ᵇ`
          : data[XlsNormalizefileKey.normalizeDPRack2][index],
      flow:
        data.norm_permeate_flow_flag[index] === 1
          ? `${data[XlsNormalizefileKey.normalizePermeateFlow][index]}ᵃ`
          : data[XlsNormalizefileKey.normalizePermeateFlow][index],
      salt_passage:
        data[XlsNormalizefileKey.normalizeSaltPassage] &&
        data[XlsNormalizefileKey.normalizeSaltPassage][index]
          ? data[XlsNormalizefileKey.normalizeSaltPassage][index]
          : "-",
      reason: data.cip_reason[index]?.join(",  "),
    }));
    const sortedData = getSortedData(result);
    dispatch(storeClenaingEffectiness(sortedData));
    setRowData(sortedData);
    // fetch threshold data from result
    if (data?.threshold) {
      setThreshold(data?.threshold);
      dispatch(storeClenaingEffectinessThreshold(data?.threshold));
    }
    setIsLoading(false);
    setIsNoStablePointsDialogOpen(false);
    setDisableNextButton?.(false);
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  };

  const fetchCleaningEffectivenessData = async () => {
    try {
      setIsNoStablePointsDialogOpen(false);
      setDisableNextButton?.(false);
      setIsLoading(true);
      const res = await cleaningEffectivness(selectedTrain);
      if (res && res.data?.status === "completed") {
        completed_state(res?.data);
      } else {
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
        }
        intervalIdRef.current = setInterval(function () {
          checkStatus(res.data?.task_id);
        }, 5000);
        setprocessedPercentage("1%");
        setIsLoading(true);
      }
    } catch (err) {
      setRowData([]);
      setIsLoading(false);
      setDisableNextButton?.(false);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.fetchFail"),
        "",
      );
    }
  };

  useEffect(() => {
    if (plantId) {
      fetchCleaningEffectivenessData();
    }

    // Cleanup function to clear interval when component unmounts
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, []);

  const checkStatus = async (task_id) => {
    try {
      const effectivenessStatus = await cleaningEffectivnessStatus(
        task_id,
        selectedTrain,
      );
      const data = effectivenessStatus?.data;
      const { status, processed, error } = data || {};

      const isNoStablePointsError =
        error === STATUS_API_ERROR_KEYS.NO_STABLE_POINTS;

      try {
        if (status === "completed") {
          completed_state(data);
        } else if (status === "pending") {
          setIsLoading(false);
          setDisableNextButton?.(false);
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          setprocessedPercentage("");
        } else if (status === "running") {
          setIsLoading(true);
          setDisableNextButton?.(false);
          setprocessedPercentage(processed);
        } else if (status === "failed") {
          setIsLoading(false);
          // setprocessedPercentage("");
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }

          if (isNoStablePointsError) {
            setDisableNextButton?.(true);
            setIsNoStablePointsDialogOpen(true);
          } else {
            setDisableNextButton?.(false);
            createNotification(
              ERROR_NOTIFICATION,
              error || t("notificationStrings.fetchFail"),
              "",
            );
          }

          setRowData([]);
        }
      } catch (error) {
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        setRowData([]);
        setIsLoading(false);
        setDisableNextButton?.(false);
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.fetchFail"),
          "",
        );
      }
    } catch (err) {
      if (err.code === "ERR_NETWORK" || err.response.status === 502) {
        console.log("Error is " + err);
      } else {
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        setRowData([]);
        setIsLoading(false);
        setDisableNextButton?.(false);
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.fetchFail"),
          "",
        );
      }
    }
  };
  const hasInvalidData = rowData?.some(
    (data) =>
      data.flow === null ||
      isNaN(data.flow) ||
      data.pressure_drop === null ||
      isNaN(data.pressure_drop) ||
      data.salt_passage === null ||
      isNaN(data.salt_passage),
  );

  const hasValuesGreaterThan100 = rowData?.some(
    (data) =>
      data.flow > 100 || data.pressure_drop > 100 || data.salt_passage > 100,
  );

  const hasValuesLessThan0 = rowData?.some(
    (data) => data.flow < 0 || data.pressure_drop < 0 || data.salt_passage < 0,
  );

  const hasSuperscript = (rowData, superscript) => {
    return rowData?.some(
      (data) =>
        (typeof data.flow === "string" && data.flow.includes(superscript)) ||
        (typeof data.pressure_drop === "string" &&
          data.pressure_drop.includes(superscript)),
    );
  };
  const hasSuperscriptb = hasSuperscript(rowData, "ᵇ");
  const hasSuperscripta = hasSuperscript(rowData, "ᵃ");
  const hasRows = Array.isArray(rowData) && rowData.length > 0;

  const disclaimerList = useMemo(() => {
    const disclaimerRules = [
      [hasRows, "cleaningEffectivnessStrings.note"],
      [
        hasValuesGreaterThan100,
        "cleaningEffectivnessStrings.noteForValuesrGeaterThan100",
      ],
      [
        hasValuesLessThan0,
        "cleaningEffectivnessStrings.noteForValuesLessThan0",
      ],
      [hasInvalidData, "cleaningEffectivnessStrings.noteWith*"],
      [hasSuperscripta, "cleaningEffectivnessStrings.notewithsuperscripta"],
      [hasSuperscriptb, "cleaningEffectivnessStrings.notewithsuperscriptb"],
    ];

    return disclaimerRules
      .filter(([isVisible]) => Boolean(isVisible))
      .map(([, translationKey]) => t(translationKey));
  }, [
    hasRows,
    hasValuesGreaterThan100,
    hasValuesLessThan0,
    hasInvalidData,
    hasSuperscripta,
    hasSuperscriptb,
    t,
  ]);

  const handleCustomModalOpen = () => {
    setIsHelpDialogOpen(true);
  };

  const handleCustomModalClose = () => {
    setIsHelpDialogOpen(false);
  };

  return (
    <div>
      <div className="descriptionText marginTop10 marginBtm10">
        {t("cleaningEffectivnessStrings.cleaningDescription")}
      </div>

      {isLoading || !rowData ? (
        <PageLoading
          customHeight={"100px"}
          iconHeight={"height--100"}
          message={t("ValueStrings.loaderMessage")}
          percentage={processedPercentage}
        ></PageLoading>
      ) : (
        <>
          <div className="chart">
            <Charts
              plantId={plantId}
              cleaningResponse={cleaningResponse}
              date={date}
              cips={cips}
              threshold={threshold}
              activeStep={activeStep}
            />
          </div>

          <div className="ro-card-wrapper">
            <div className="ro-card-header">
              <div className="info-section">
                <div className="teal-heading">
                  {t("cleaningEffectivnessStrings.cleaningEffectivenessValues")}
                </div>
                <IconWithTooltip
                  label={t(
                    "helpInformation.historicalCIPEffectivenessTableHelp",
                  )}
                />
              </div>
              <IconButton id="help-dialog-btn" onClick={handleCustomModalOpen}>
                <QuestionIcon />
              </IconButton>
            </div>

            <div className="ro-ag-table">
              <WPAgGrid
                gridRef={gridRef}
                columnDefs={colDefs}
                rowData={rowData}
                exportFileName={t(
                  "cleaningEffectivnessStrings.cleaningEffectivenessValues",
                )}
                disclaimerList={disclaimerList}
              />
            </div>

            <div>
              {disclaimerList.map((line) => (
                <div
                  key={line}
                  className="descriptionText marginTop10 marginBtm10"
                >
                  {line}
                </div>
              ))}
            </div>
          </div>

          <hr />

          <div className="ro-disclaimer">
            {t("disclaimer", { currentYear })}
          </div>
        </>
      )}

      {isHelpDialogOpen && (
        <CustomModal
          show={isHelpDialogOpen}
          header={t("helpInformation.cleaningEffectivenessHelp")}
          onModalClose={handleCustomModalClose}
          confirmBtn={t("ValueStrings.okay")}
          size="small"
          width="575px"
        >
          <div className="help-dialog-content">
            <div className="help-section">
              <ul className="help-list">
                <li className="help-list-item">
                  {t("helpInformation.cleaningEffectivenessMainText")}
                  <ul className="help-nested-list">
                    <li className="help-nested-list-item">
                      {t("helpInformation.cleaningEffectivenessReducePoints")}
                    </li>
                    <li className="help-nested-list-item">
                      {t("helpInformation.cleaningEffectivenessReduceDays")}
                    </li>
                  </ul>
                </li>
                <li className="help-list-item">
                  {t("helpInformation.cleaningEffectivenessIncreaseDays")}
                </li>
              </ul>
            </div>
          </div>
        </CustomModal>
      )}

      {isSettingsDialogOpen && (
        <AdvanceSettings
          isSettingsDialogOpen={isSettingsDialogOpen}
          handleSettingsDialogClose={() => handleSettingsDialog(false)}
          selectedTrain={selectedTrain}
          cleaningEffectivenessCalculation
          onSave={fetchCleaningEffectivenessData}
        />
      )}

      <NoStablePointsDialog
        show={isNoStablePointsDialogOpen}
        onClose={() => setIsNoStablePointsDialogOpen(false)}
      />
    </div>
  );
};

export default HistoricalCIPEffectivness;
